import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Smartphone, Search } from 'lucide-react';
import { TOOLS, CATEGORIES } from '@/lib/tools';
import { useSeo } from '@/lib/seo';
import { AdSlot } from '@/components/AdSlot';

const featured = ['percentage-calculator', 'loan-calculator', 'age-calculator', 'tip-calculator', 'bmi-calculator', 'unit-converter'];

export default function HomePage() {
  useSeo({
    title: 'Free Online Calculators for Math, Money & More',
    description: 'Use 15 free online calculators for percentages, loans, dates, tips, BMI, currency and unit conversions. Get instant answers, formulas and worked examples.',
    path: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'CalculateEasy',
      url: (typeof window !== 'undefined' ? window.location.origin : ''),
      potentialAction: {
        '@type': 'SearchAction',
        target: (typeof window !== 'undefined' ? window.location.origin : '') + '/tools?q={query}',
        'query-input': 'required name=query',
      },
    },
  });

  const featuredTools = featured.map((s) => TOOLS.find((t) => t.slug === s)).filter(Boolean);

  return (
    <>
      <section className="hero-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-medium shadow-sm mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              15 tools live · more launching weekly
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Free online calculators for everyday questions.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Percentages, loans, tips, ages, BMIs — CalculateEasy gives you fast, accurate answers with the formula and worked examples right below. No sign-up. No ads on the buttons.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/tools"
                data-testid="hero-cta-browse"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 shadow-sm hover:shadow-md transition-all"
              >
                Browse all tools <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/categories"
                data-testid="hero-cta-categories"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white border border-slate-200 text-slate-900 font-semibold hover:bg-slate-50 transition-colors"
              >
                Explore categories
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-semibold text-slate-900">Popular calculators</h2>
            <p className="text-slate-600 mt-1">Hand-picked tools that people use most.</p>
          </div>
          <Link to="/tools" className="hidden sm:inline-flex text-sm font-medium text-emerald-700 hover:text-emerald-800">See all →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredTools.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.slug}
                to={`/tools/${t.slug}`}
                data-testid={`home-tool-card-${t.slug}`}
                className="group bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors">{t.name}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t.tagline}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-emerald-700 font-medium">
                  Open tool <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-8">
          <Feature Icon={Zap} title="Instant, offline-friendly math" body="Every calculator runs in your browser — results update as you type." />
          <Feature Icon={Smartphone} title="Designed mobile-first" body="Big touch targets, readable results, and layouts that fit phones and tablets." />
          <Feature Icon={Shield} title="No dark patterns" body="We label ad space clearly and keep it away from the buttons and inputs." />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-semibold text-slate-900 mb-2">Browse by category</h2>
        <p className="text-slate-600 mb-8">Everyday math, money, time, conversions, and health.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.id}
              to={`/categories#${c.id}`}
              data-testid={`home-category-${c.id}`}
              className={`group rounded-2xl p-6 text-white bg-gradient-to-br ${c.color} hover:brightness-110 transition-all`}
            >
              <div className="font-semibold text-lg">{c.name}</div>
              <div className="text-white/80 text-sm mt-1 leading-relaxed">{c.description}</div>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium">
                Explore <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <AdSlot size="leaderboard" id="ad-slot-home-bottom" />
      </div>
    </>
  );
}

function Feature({ Icon, title, body }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
    </div>
  );
}
